import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { OverviewDisplayComponent } from '@products/overview/overview-display-component.interface';
import { OverviewComponentConfig } from '@products/overview/overview-component.config';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { ProductDetailConfiguration } from '@types';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { FundContentService } from '@products/overview/services/fund-content.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { Logger } from '@utils/logger';
import { FundOverviewService } from '@products/services/fund-overview.service';
import CanadaRiskQuery from '@graphql/overview/canada-risk-classifciation.graphql';
import { SiteConfigService } from '@services';
import get from 'lodash/get';
import { TranslateService } from '@shared/translate/translate.service';
import { takeUntil } from 'rxjs/operators';
import { ResponsiveService, StringUtilsService } from '@frk/eds-components';
import { ViewModeService } from '@services/view-mode.service';
import { CompareFn } from '@utils/sort-utils';

const logger = Logger.getLogger('FundContentComponent');

export interface CanadaRiskSummary {
  elemname: string;
  elemnamestd: string;
  elemvalue: string;
}

export interface CanadaRiskData {
  Summary: CanadaRiskSummary[];
}

export interface CanadaRiskObject {
  data: CanadaRiskData;
  loading: boolean;
  networkStatus: number;
}

@Component({
  selector: 'ft-fund-content',
  templateUrl: './fund-content.component.html',
  styleUrls: ['./fund-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundContentComponent
  extends BaseProductDetailComponent
  implements OverviewDisplayComponent, OnDestroy {
  public documents: DocumentData[] = [];
  public dataSubscription: Subscription;
  public loadVideoflag: boolean;
  public isCanada: boolean;
  public strRiskFactorScale: string[] = [];
  public isMobile$: Observable<boolean>;
  public isHandheld$: Observable<boolean>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public isEditMode = false;

  constructor(
    private viewModeService: ViewModeService,
    private fundContentService: FundContentService,
    private changeDetector: ChangeDetectorRef,
    private fundOverviewService: FundOverviewService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private stringUtilsService: StringUtilsService,
    private responsiveService: ResponsiveService
  ) {
    super();
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  populate(
    componentConfig: OverviewComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.isCanada = this.siteConfigService.isCanada();

    this.initialize(brConfig, component, page);
    const documents = component.getModels()?.documents;
    const fundContentDocs = documents?.map((doc) =>
      page.getContent(doc)?.getData<DocumentData>()
    );
    if (this.brConfig.fundId) {
      this.dataSubscription = this.fundContentService
        .populate(
          {
            fundId: this.brConfig.fundId,
            shareClassCode: this.brConfig.shareClassCode,
          },
          fundContentDocs,
          this.brConfig.location
        )
        .subscribe((docs: DocumentData[]) => {
          if (this.isCanada) {
            // Fetch String Array of scales from Labels
            this.strRiskFactorScale = [
              this.translateService.instant(
                'products.risk-factor-ca-low',
                brConfig.fundId
              ),
              this.translateService.instant(
                'products.risk-factor-ca-low-to-medium',
                brConfig.fundId
              ),
              this.translateService.instant(
                'products.risk-factor-ca-medium',
                brConfig.fundId
              ),
              this.translateService.instant(
                'products.risk-factor-ca-medium-to-high',
                brConfig.fundId
              ),
              this.translateService.instant(
                'products.risk-factor-ca-high',
                brConfig.fundId
              ),
            ];
            // Fetch Risk Summary from an additional query
            this.fundOverviewService
              .registerWithoutMapper(CanadaRiskQuery, {
                fundId: this.brConfig.fundId,
                shareClassCode: this.brConfig.shareClassCode,
              })
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((data: CanadaRiskObject) => {
                const riskSummary: CanadaRiskSummary = get(
                  data,
                  'data.Summary',
                  []
                ).find(
                  (summary) =>
                    summary.elemnamestd === 'RiskClassification_Txt_Local'
                );
                docs.forEach((doc) => {
                  if (doc.showRiskFactor) {
                    doc.strRank = riskSummary?.elemvalue; // Assign String Rank value
                  }
                });
                this.documents = docs;
                this.addTabId();
                logger.debug(
                  `documents for ${this.brConfig.location}`,
                  this.documents
                );
                this.documents = this.documents.sort(this.sortDocuments);
                this.changeDetector.detectChanges();
              });
          } else {
            this.documents = docs;
            this.addTabId();
            logger.debug(
              `documents for ${this.brConfig.location}`,
              this.documents
            );
            this.documents = this.documents.sort(this.sortDocuments);
            this.changeDetector.detectChanges();
          }
        });
    }
  }

  onSelected(tabId: string) {
    let contentBlock = [];
    this.loadVideoflag = false;
    this.documents.forEach((content: any) => {
      if (content.title === tabId) {
        contentBlock = content.contentblocks;
        contentBlock.forEach((item) => {
          if (item.itemsTwo && item.itemsTwo[0].type === 'EmbeddedVideo') {
            this.loadVideoflag = true;
          }
        });
      }
    });
    this.changeDetector.detectChanges();
  }

  // This method derving tabId from category.
  addTabId(): void {
    this.documents.forEach((doc) => {
      doc.tabId = this.stringUtilsService.generateNewId(doc?.category);
      // updating tab id for marketing content tab as its conflicting with section defined in product detail layout
      // which causes scroll issue on selecting marketing content tab (small device)
      doc.tabId =
        doc.tabId === 'marketing-content' ? 'marketing-content-tab' : doc.tabId;
    });
  }

  /**
   * Handling the displayAsAFirstTab flag, when it comes true then,
   * We show it first and remaining order will preserve/show as it is coming from API.
   *
   */
  private sortDocuments: CompareFn<DocumentData> = (a, b) => {
    if (a.displayAsAFirstTab) {
      return -1;
    }
    if (b.displayAsAFirstTab) {
      return 1;
    }
    return 0;
  };

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
